import type {
  ErrorContext,
  ErrorEnvironment,
  ErrorPlatform,
  ErrorReportPayload,
  ErrorSeverity,
} from "@ai-teach/types";

/**
 * Configuration for ErrorReporter
 * Must be provided by each platform via init()
 */
interface ErrorReporterConfig {
  platform: ErrorPlatform;
  appVersion: string;
  apiUrl: string;
  environment: ErrorEnvironment;
  getAuthToken?: () => Promise<string | null>;
}

/**
 * ErrorReporter - Platform-agnostic error logging utility
 *
 * Usage:
 * 1. Initialize in each app:
 *    ErrorReporter.init({ platform: 'web', appVersion: '1.0.0', ... })
 *
 * 2. Log errors:
 *    ErrorReporter.log(error, { action: 'login', screen: '/login' })
 *    ErrorReporter.warn('Deprecation warning', { feature: 'oldApi' })
 *    ErrorReporter.critical(error, { action: 'payment' })
 */
class ErrorReporterClass {
  private config: ErrorReporterConfig | null = null;
  private isInitialized = false;

  /**
   * Initialize the error reporter
   * Must be called before any error logging
   *
   * @param config - Platform-specific configuration
   */
  init(config: ErrorReporterConfig): void {
    this.config = config;
    this.isInitialized = true;
  }

  /**
   * Log an error with default severity
   *
   * @param error - Error object or message
   * @param context - Additional context for debugging
   */
  async log(error: Error | string, context?: ErrorContext): Promise<void> {
    await this.report(error, { ...context, severity: "error" });
  }

  /**
   * Log a warning
   *
   * @param message - Warning message
   * @param context - Additional context
   */
  async warn(message: string, context?: ErrorContext): Promise<void> {
    const error = new Error(message);
    error.name = "Warning";
    await this.report(error, { ...context, severity: "warning" });
  }

  /**
   * Log a critical error
   *
   * @param error - Critical error object or message
   * @param context - Additional context
   */
  async critical(error: Error | string, context?: ErrorContext): Promise<void> {
    await this.report(error, { ...context, severity: "critical" });
  }

  /**
   * Internal method to send error report to API
   *
   * @param error - Error object or message
   * @param context - Context including severity
   */
  private async report(
    error: Error | string,
    context: ErrorContext & { severity: ErrorSeverity }
  ): Promise<void> {
    if (!this.isInitialized || !this.config) {
      console.error("ErrorReporter not initialized. Call ErrorReporter.init() first.");
      return;
    }

    try {
      const errorObj = typeof error === "string" ? new Error(error) : error;
      const userAgent = this.getUserAgent();

      const payload: ErrorReportPayload = {
        message: errorObj.message,
        ...(errorObj.stack ? { stack: errorObj.stack } : {}),
        errorType: errorObj.name || "Error",
        platform: this.config.platform,
        screen: context.screen || this.getCurrentScreen(),
        ...(context.action ? { action: context.action } : {}),
        ...(userAgent ? { userAgent } : {}),
        environment: this.config.environment,
        appVersion: this.config.appVersion,
        severity: context.severity,
        ...(context.metadata ? { metadata: context.metadata } : {}),
      };

      // Get auth token if available
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      if (this.config.getAuthToken) {
        const token = await this.config.getAuthToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }

      // Send to API (fire and forget - don't block on errors)
      fetch(`${this.config.apiUrl}/api/errors`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
        credentials: "include",
      }).catch((err: unknown) => {
        // Silently fail - don't crash if error logging fails
        console.warn("Failed to log error:", err);
      });
    } catch (err: unknown) {
      // Don't crash if error reporting fails
      console.error("ErrorReporter.report failed:", err);
    }
  }

  /**
   * Get current screen/route
   * Implemented differently per platform
   */
  private getCurrentScreen(): string {
    if (typeof window !== "undefined") {
      // Web - use window.location
      return window.location.pathname;
    }
    // Mobile or server - return unknown
    return "unknown";
  }

  /**
   * Get user agent string
   * Platform-specific
   */
  private getUserAgent(): string | undefined {
    if (typeof navigator !== "undefined") {
      return navigator.userAgent;
    }
    return undefined;
  }
}

/**
 * Singleton instance of ErrorReporter
 * Export this for use across the application
 */
export const ErrorReporter = new ErrorReporterClass();
